import React, { createContext, ReactNode, useCallback, useState } from 'react'

interface OpenCloseProviderProps {
  children: ReactNode
  initialValue?: boolean
}

const defaultValue = {
  isOpen: true
}

export interface OpenCloseStateContextProps {
  isOpen: boolean
}

export interface OpenCloseUpdaterContextProps {
  open: () => void
  close: () => void
}

const OpenCloseStateContext = createContext<boolean>({} as boolean)
const OpenCloseUpdaterContext = createContext<(newState: boolean) => void>({} as (newState: boolean) => void)

const OpenCloseProvider = ({ children, initialValue = defaultValue.isOpen }: OpenCloseProviderProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(initialValue)

  return (
    <OpenCloseStateContext.Provider value={isOpen}>
      <OpenCloseUpdaterContext.Provider value={setIsOpen}>{children}</OpenCloseUpdaterContext.Provider>
    </OpenCloseStateContext.Provider>
  )
}

const useOpenCloseState = (): OpenCloseStateContextProps => {
  const isOpen = React.useContext(OpenCloseStateContext)

  return { isOpen }
}

const useOpenCloseUpdater = (): OpenCloseUpdaterContextProps => {
  const setIsOpen = React.useContext(OpenCloseUpdaterContext)

  const open = useCallback(() => setIsOpen(true), [setIsOpen])
  const close = useCallback(() => setIsOpen(false), [setIsOpen])

  return { open, close }
}

export { OpenCloseStateContext, OpenCloseUpdaterContext, OpenCloseProvider, useOpenCloseState, useOpenCloseUpdater }
